import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Link,
    IconButton,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Sponsor logos (keep your existing paths)
import ahaLogo from '../assets/sponsors/aha.png';
import alabamaTravel from '../assets/sponsors/alabama-travel.JPG';
import cityOfPiedmont from '../assets/sponsors/city-Of-Piedmont.png';
import communityFoundation from '../assets/sponsors/communityFoundation.png';
import countyCommission from '../assets/sponsors/county-Commision.png';
import danielFoundation from '../assets/sponsors/danielFoundation.png';
import historicalCommission from '../assets/sponsors/historical-Comm.png';
import norfolkSouthern from '../assets/sponsors/norfolkSouthern.png';
import rcdLogo from '../assets/sponsors/rcd.png';

const sponsors = [
    { name: 'AHA', src: ahaLogo, url: 'https://alabamahumanities.org/' },
    { name: 'Alabama Travel', src: alabamaTravel, url: 'https://alabama.travel/' },
    { name: 'City of Piedmont', src: cityOfPiedmont, url: 'https://www.piedmontcity.org/' },
    { name: 'Community Foundation of NE Alabama', src: communityFoundation, url: 'https://www.cfnea.org/' },
    { name: 'Calhoun County Commission', src: countyCommission, url: 'https://www.calhouncounty.org/' },
    { name: 'The Daniel Foundation', src: danielFoundation, url: 'https://danielfoundationofalabama.com/' },
    { name: 'Alabama Historical Commission', src: historicalCommission, url: 'https://ahc.alabama.gov/' },
    { name: 'Norfolk Southern', src: norfolkSouthern, url: 'https://www.norfolksouthern.com/' },
    { name: 'Coosa Valley RC&D', src: rcdLogo, url: 'https://alabamarcd.org/about-us/' },
];

const nudgeRight = keyframes`
    0% { transform: translateX(0); }
    50% { transform: translateX(6px); }
    100% { transform: translateX(0); }
`;

export default function Sponsors() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const railRef = useRef(null);

    // Auto‑scroll the sponsor rail when visible (mobile only)
    useEffect(() => {
        const rail = railRef.current;
        if (!rail || !isMobile) return;

        let raf = 0;
        let running = false;

        const step = () => {
            if (!running) return;
            rail.scrollLeft += 0.7;
            if (rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1) {
                rail.scrollLeft = 0;
            }
            raf = requestAnimationFrame(step);
        };

        const io = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                running = true;
                raf = requestAnimationFrame(step);
            } else {
                running = false;
                cancelAnimationFrame(raf);
            }
        }, { threshold: 0.25 });

        io.observe(rail);

        const stop = () => { running = false; cancelAnimationFrame(raf); };
        const start = () => { if (!running) { running = true; raf = requestAnimationFrame(step); } };

        rail.addEventListener('pointerdown', stop);
        rail.addEventListener('mouseenter', stop);
        rail.addEventListener('pointerup', start);
        rail.addEventListener('mouseleave', start);

        return () => {
            io.disconnect();
            cancelAnimationFrame(raf);
            rail.removeEventListener('pointerdown', stop);
            rail.removeEventListener('mouseenter', stop);
            rail.removeEventListener('pointerup', start);
            rail.removeEventListener('mouseleave', start);
        };
    }, [isMobile]);

    const scrollBy = (delta) => {
        const el = railRef.current;
        if (!el) return;
        el.scrollBy({ left: delta, behavior: 'smooth' });
    };

    const [arrowsInView, setArrowsInView] = useState(false);
    const arrowsRef = useRef(null);
    useEffect(() => {
        if (!isMobile || !arrowsRef.current) return;
        const io = new IntersectionObserver(([entry]) => {
            setArrowsInView(entry.isIntersecting);
        }, { threshold: 0.25 });
        io.observe(arrowsRef.current);
        return () => io.disconnect();
    }, [isMobile]);

    return (
        <Box component="section" sx={{ py: 4, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
            <Container maxWidth="lg">
                <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'text.secondary' }}>
                    Our Partners & Sponsors
                </Typography>

                {/* Logo rail */}
                <Box
                    ref={railRef}
                    sx={{
                        mt: 2,
                        display: 'flex',
                        gap: { xs: 3, sm: 4 },
                        overflowX: { xs: 'auto', md: 'visible' },
                        px: { xs: 1, md: 0 },
                        scrollSnapType: { xs: 'x mandatory', md: 'none' },
                        '&::-webkit-scrollbar': { display: 'none' },
                        maskImage: { xs: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)', md: 'none' },
                    }}
                >
                    {sponsors.map(({ name, src, url }) => (
                        <Link
                            key={name}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            aria-label={name}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                scrollSnapAlign: { xs: 'center', md: 'unset' },
                                minWidth: { xs: 160, sm: 180, md: 0 },
                                p: { xs: 1.25, md: 0 },
                                borderRadius: 2,
                                bgcolor: { xs: 'common.white', md: 'transparent' },
                                boxShadow: { xs: 1, md: 0 },
                            }}
                        >
                            <Box
                                component="img"
                                src={src}
                                alt={name}
                                loading="lazy"
                                decoding="async"
                                sx={{ height: { xs: 44, sm: 54, md: 40 }, objectFit: 'contain' }}
                            />
                        </Link>
                    ))}
                </Box>

                {/* Arrows under the rail: MOBILE ONLY */}
                {isMobile && (
                    <Box ref={arrowsRef} sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                        <IconButton
                            aria-label="Previous sponsors"
                            onClick={() => scrollBy(-260)}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                '& .MuiSvgIcon-root': { animation: arrowsInView ? `${nudgeRight} 1.2s ease-in-out infinite` : 'none' },
                            }}
                        >
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            aria-label="Next sponsors"
                            onClick={() => scrollBy(260)}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                boxShadow: 1,
                                '& .MuiSvgIcon-root': { animation: arrowsInView ? `${nudgeRight} 1.2s ease-in-out infinite` : 'none' },
                            }}
                        >
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Container>
        </Box>
    );
}
